import { useEffect, useState, useCallback, useRef } from "react";
import { api, WS_BASE } from "@/lib/api";
import { prepareAutoPrintTab, finishAutoPrint } from "@/lib/autoPrint";
import { usePlant } from "@/context/PlantContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Radio, Factory, ExternalLink, RefreshCw, Activity, Maximize, History, Brain, Volume2, VolumeX, Tablet } from "lucide-react";
import { armAudio, audioArmed, autoArmOnFirstGesture, VOICE_PERSONAS, getPersonaId, previewVoice, describeVoice } from "@/lib/voice";
import SlumpCard from "@/components/SlumpCard";
import { toast } from "sonner";
import { metaFor } from "@/lib/materials";
import { LANES } from "@/lib/batching";
import BatchDetailDialog from "@/components/BatchDetailDialog";
import { MixBar, PlantColumn, TruckQueue, LiveWeights } from "./BatcherCockpit";

// ─── Batcher Console · v4.4 "Cockpit" ───────────────────────────────
// Zero-scroll two-column monitoring layout. Container below owns state +
// API handlers; the visual pieces live in ./BatcherCockpit.jsx.

// ─── Live scale feed — polls /plant-agent/status every 1 s ─────────
// Returns { online, lastSeen, scaleById } so any child can look up a live
// weight_kg + stable flag by scale id (e.g. scaleById["cement"]).
export function useScaleFeed(plantId) {
  const [feed, setFeed] = useState({ online: false, agentOnline: false, state: "offline", lastSeen: null, scaleById: {}, resolvedPid: "", statusText: "" });
  const failures = useRef(0);
  // Remember the last time we had a genuinely LIVE feed. Production runs 2 replicas,
  // so a poll can round-robin to the pod that hasn't received the plant-agent's
  // readings and reply online:false — that made the banner FLASH LIVE/OFFLINE. We
  // hold the last live feed through a short grace window so a single offline/empty
  // poll (pod split, redeploy, dropped request) can't flip the pill.
  const lastGood = useRef({ at: 0, scaleById: {}, agentOnline: false, lastSeen: null, resolvedPid: "", state: "offline" });
  const GRACE_MS = 6000;
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const q = plantId ? `?plant_id=${plantId}` : "";
        const r = await api.get(`/plant-agent/status${q}`);
        if (!alive) return;
        failures.current = 0;
        const scaleById = {};
        for (const s of (r.data.readings || [])) scaleById[s.scale_id] = s;
        const parts = [];
        parts.push(`state: ${r.data.state || "offline"}`);
        if (r.data.plant_id !== r.data.requested_plant_id) {
          parts.push(`resolved plant_id: ${r.data.plant_id || "—"}`);
        }
        (r.data.scale_status || []).forEach((s) => {
          parts.push(`${s.label || s.scale_id}: ${s.state}${s.detail ? " · " + s.detail : ""}`);
        });
        if (r.data.last_seen) parts.push(`last reading ${new Date(r.data.last_seen).toLocaleTimeString()}`);
        else if (r.data.agent_seen) parts.push(`agent seen ${new Date(r.data.agent_seen).toLocaleTimeString()}`);
        const statusText = parts.join("\n") || "No plant-agent handshake yet";
        if (r.data.online) {
          lastGood.current = { at: Date.now(), scaleById, agentOnline: !!r.data.agent_online, lastSeen: r.data.last_seen, resolvedPid: r.data.plant_id || "", state: r.data.state || "streaming" };
          setFeed({ online: true, agentOnline: !!r.data.agent_online, state: r.data.state || "streaming", lastSeen: r.data.last_seen, scaleById, resolvedPid: r.data.plant_id || "", statusText });
        } else if (Date.now() - lastGood.current.at < GRACE_MS) {
          // Successful poll but THIS replica has no fresh reading — keep the last
          // live feed visible for the grace window instead of flashing OFFLINE.
          setFeed((f) => ({ ...f, online: true, agentOnline: lastGood.current.agentOnline, state: "streaming", lastSeen: lastGood.current.lastSeen, scaleById: lastGood.current.scaleById, resolvedPid: lastGood.current.resolvedPid, statusText }));
        } else {
          setFeed({ online: false, agentOnline: !!r.data.agent_online, state: r.data.state || "offline", lastSeen: r.data.last_seen, scaleById, resolvedPid: r.data.plant_id || "", statusText });
        }
      } catch {
        // One dropped poll is a network blip, not a dead scale. Only give up after
        // several consecutive failures AND the grace window has elapsed.
        failures.current += 1;
        if (alive && failures.current >= 8 && Date.now() - lastGood.current.at >= GRACE_MS) {
          setFeed((f) => ({ ...f, online: false, agentOnline: false, state: "offline" }));
        }
      }
    };
    tick();
    // 400 ms: on a fast agg gate a 1 s poll left the number up to 60 kg behind the
    // material, so the operator was accepting a stale weight.
    const t = setInterval(tick, 400);
    return () => { alive = false; clearInterval(t); };
  }, [plantId]);
  return feed;
}

export default function Batcher() {
  const { plantId } = usePlant();
  const [batches, setBatches] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [liveStatus, setLiveStatus] = useState("connecting");
  const scaleFeed = useScaleFeed(plantId);
  const wsRef = useRef(null);
  const debounceRef = useRef(null);
  // v3.5p — Load Recall dialog state
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallRange, setRecallRange] = useState("today");
  const [recallRows, setRecallRows] = useState([]);
  const [recallLoading, setRecallLoading] = useState(false);
  const [detail, setDetail] = useState(null);   // { batch, mode: "weights" | "tolerance" }
  // v3.5r — AI Slump card behind an icon-triggered dialog
  const [slumpOpen, setSlumpOpen] = useState(false);
  // Cockpit — which lane the mix bar + live weights follow
  const [selectedLane, setSelectedLane] = useState(LANES[0]);
  const [laneTouched, setLaneTouched] = useState(false);
  // Windows blocks every sound until the page has had a click. The first tap anywhere
  // arms it silently; the header button lets the operator prove it works.
  const [voiceId, setVoiceId] = useState(getPersonaId());
  const [soundOn, setSoundOn] = useState(audioArmed());
  useEffect(() => {
    const off = autoArmOnFirstGesture();
    const t = setInterval(() => { if (audioArmed()) { setSoundOn(true); clearInterval(t); } }, 1000);
    return () => { off(); clearInterval(t); };
  }, []);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const q = plantId ? `?plant_id=${plantId}` : "";
    const seq = ++loadSeq.current;
    const [b, m] = await Promise.all([
      api.get(`/batches${q}`),
      api.get(`/mixes`),
    ]);
    if (seq !== loadSeq.current) return;   // a newer refresh already landed — drop this stale snapshot
    setBatches(b.data);
    setMixes(m.data || []);
  }, [plantId]);

  useEffect(() => { load(); const t = setInterval(load, 10_000); return () => clearInterval(t); }, [load]);

  // v3.5p — fetch completed loads whenever the recall dialog opens or the
  // day/month toggle flips. Uses the new /batches filters so we get every
  // load for the range, not just the last 500.
  useEffect(() => {
    if (!recallOpen) return;
    let cancelled = false;
    (async () => {
      setRecallLoading(true);
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const from = recallRange === "today" ? `${yyyy}-${mm}-${dd}` : `${yyyy}-${mm}-01`;
      const to   = `${yyyy}-${mm}-${dd}`;
      const params = new URLSearchParams({ status: "complete", date_from: from, date_to: to, limit: "2000" });
      if (plantId) params.set("plant_id", plantId);
      try {
        const r = await api.get(`/batches?${params.toString()}`);
        if (!cancelled) setRecallRows(r.data || []);
      } catch { if (!cancelled) setRecallRows([]); }
      if (!cancelled) setRecallLoading(false);
    })();
    return () => { cancelled = true; };
  }, [recallOpen, recallRange, plantId]);

  // Live refresh via existing dispatch WS — batch.* events already fire from the API
  useEffect(() => {
    let ws;
    let closed = false;
    let retry = 0;
    const debouncedReload = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(load, 300);
    };
    const connect = () => {
      if (closed) return;
      setLiveStatus("connecting");
      try { ws = new WebSocket(`${WS_BASE}/ws/dispatch`); }
      catch { setLiveStatus("offline"); return; }
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; setLiveStatus("live"); };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (plantId && evt.plant_id && evt.plant_id !== plantId) return;
          if ((evt.type || "").startsWith("batch.")) debouncedReload();
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setLiveStatus("offline");
        if (closed) return;
        retry = Math.min(retry + 1, 5);
        setTimeout(connect, 1000 * retry);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      closed = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [load, plantId]);

  // Group by lane
  const active = batches.filter((b) => ["queued", "batching"].includes(b.status));
  const byLane = { "LANE-1": null, "LANE-2": null };
  for (const b of active) {
    if (b.lane_id && (b.lane_id in byLane) && !byLane[b.lane_id]) byLane[b.lane_id] = b;
  }
  const queue = active.filter((b) => !b.lane_id);

  const assign = async (lane, batchId, silo) => {
    try {
      await api.post(`/batches/${batchId}/assign-lane`, { lane_id: lane, cement_silo_id: silo || null });
      // Start the batch automatically when it lands on a lane
      const b = active.find((x) => x.id === batchId);
      if (b && b.status === "queued") await api.post(`/batches/${batchId}/start`);
      toast.success(`${lane} · batch loaded`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setSilo = async (batchId, silo) => {
    try {
      await api.post(`/batches/${batchId}/assign-lane`, { lane_id: byLaneIdOf(batches, batchId), cement_silo_id: silo });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAction = async (batchId, payload) => {
    try {
      await api.post(`/batches/${batchId}/action`, payload);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const capture = async (batchId, key, value) => {
    try {
      await api.post(`/batches/${batchId}/action`, {
        action: `capture_${key}`,
        material: key,
        actual: value,
        note: "Scale reading captured",
      });
      toast.success(`${metaFor(key).label} · ${value}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeMix = async (batchId, mixId) => {
    try {
      await api.patch(`/batches/${batchId}`, { mix_id: mixId });
      toast.success("Mix design changed — targets recomputed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change mix"); }
  };

  const completingRef = useRef(new Set());
  const complete = async (batchId, auto = false) => {
    // `auto` fires from the cockpit when everything is weighed and the agg bin has
    // emptied into the truck — no confirm dialog to click in that case.
    // Auto-complete and a manual press can race — one request per batch, ever.
    if (completingRef.current.has(batchId)) return;
    if (!auto && !window.confirm("Complete this load and issue the docket?")) return;
    completingRef.current.add(batchId);
    const printTab = prepareAutoPrintTab();
    try {
      const res = await api.post(`/batches/${batchId}/complete`);
      if (res.data.duplicate) {
        finishAutoPrint(printTab, null);
        toast.info(`Load already completed · docket ${res.data.docket?.docket_no || ""}`);
      } else {
        finishAutoPrint(printTab, res.data.auto_print === false ? null : res.data.docket);
        toast.success(`Docket ${res.data.docket?.docket_no || ""} issued`);
      }
      load();
    } catch (e) { finishAutoPrint(printTab, null); toast.error(e.response?.data?.detail || "Failed"); }
  };

  const abort = async (batchId) => {
    const reason = window.prompt("E-STOP reason (optional):", "Batcher stopped");
    if (reason === null) return;
    try {
      await api.post(`/batches/${batchId}/abort`, { reason });
      toast.success("Batch aborted — truck released, no inventory deducted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const loadTruck = async (batchId) => {
    try {
      await api.post(`/batches/${batchId}/action`, {
        action: "load_truck",
        note: "Discharge sequence started — water/admix → 50% agg → cement → 50% agg",
      });
      toast.success("Load Truck signalled to loader operator");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Auto-follow the lane that is actually batching until the operator picks one
  const batchingLane = LANES.find((l) => byLane[l]?.status === "batching") || LANES.find((l) => byLane[l]);
  const lane = laneTouched ? selectedLane : (batchingLane || selectedLane);
  const focus = byLane[lane];
  const focusMix = focus ? mixes.find((m) => m.id === focus.mix_id) : null;
  const pickLane = (l) => { setSelectedLane(l); setLaneTouched(true); };
  const toolBtn = "inline-flex items-center gap-1 border border-zinc-700 hover:border-primary-orange text-zinc-300 hover:text-primary-orange px-2 py-1 mono text-[9px] uppercase tracking-widest";

  return (
    <div className="h-full min-h-0 flex flex-col gap-1.5 p-1.5 bg-black text-white overflow-hidden" data-testid="batcher-console">
      {/* Compact tool row */}
      <div className="flex items-center gap-2 shrink-0 px-1">
        <div className="heading text-base font-black uppercase flex items-center gap-1.5 !text-white"><Factory className="w-4 h-4 text-primary-orange" /> Batcher <span className="text-primary-orange">Cockpit</span></div>
        <span data-testid="batcher-ws-pill" className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${liveStatus === "live" ? "border-primary-orange text-primary-orange" : liveStatus === "connecting" ? "border-zinc-700 text-zinc-400" : "border-red-700 text-red-400"}`}>
          <Radio className={`w-2.5 h-2.5 ${liveStatus === "live" ? "pulse-orange" : ""}`} />{liveStatus === "live" ? "LIVE" : liveStatus === "connecting" ? "CONNECTING" : "OFFLINE"}
        </span>
        <span data-testid="rinstrum-pill" title={scaleFeed.statusText || "No plant-agent handshake yet"}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest whitespace-pre ${scaleFeed.state === "streaming" ? "border-emerald-500 text-emerald-400" : scaleFeed.state === "idle" ? "border-amber-500 text-amber-400" : "border-red-700 text-red-400"}`}>
          <Activity className={`w-2.5 h-2.5 ${scaleFeed.state === "streaming" ? "animate-pulse" : ""}`} />Rinstrum · {scaleFeed.state === "streaming" ? "LIVE" : scaleFeed.state === "idle" ? "AGENT UP · NO WEIGHTS" : "OFFLINE"}
        </span>
        <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest hidden xl:block">2 lanes · 2 × 60 t GP silos · Rinstrum R420 · live weights stream into ACTUAL — click ▲ to lock</div>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" data-testid="batcher-sound-btn" onClick={() => { armAudio(true); setSoundOn(true); }}
            title="Arm the plant audio, then test it. Windows blocks all sound until you click once on the page."
            className={`${toolBtn} ${soundOn ? "!text-emerald-400 !border-emerald-700" : "!text-amber-300 !border-amber-500 animate-pulse"}`}>
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />} {soundOn ? "Sound On" : "Enable Sound"}</button>
          <select value={voiceId} data-testid="batcher-voice-select"
            onChange={(e) => { setVoiceId(e.target.value); previewVoice(e.target.value); toast.success(`Voice: ${VOICE_PERSONAS.find((p) => p.id === e.target.value)?.label} · ${describeVoice(e.target.value)}`); }}
            title={`Announcer voice · uses ${describeVoice(voiceId)}`}
            className="mono text-[10px] bg-black border border-zinc-700 text-zinc-300 px-1.5 py-1 uppercase tracking-widest">
            {VOICE_PERSONAS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <button type="button" data-testid="batcher-refresh-btn" onClick={() => { load(); window.dispatchEvent(new CustomEvent("batcher:refresh")); toast.success("Refreshed"); }} title="Force refresh" className={toolBtn}><RefreshCw className="w-3 h-3" /> Refresh</button>
          <button type="button" data-testid="batcher-recall-btn" onClick={() => setRecallOpen(true)} title="Recall all loads for today / this month" className={toolBtn}><History className="w-3 h-3" /> Load Recall</button>
          <button type="button" data-testid="batcher-slump-btn" onClick={() => setSlumpOpen(true)} title="AI Slump Prediction" className={`${toolBtn} !text-primary-orange`}><Brain className="w-3 h-3" /> Slump AI</button>
          <button type="button" data-testid="batcher-fullscreen-btn" title="Hide the menu and go full screen" className={toolBtn}
            onClick={() => { const on = document.body.classList.toggle("batcher-fullscreen"); try { if (on) document.documentElement.requestFullscreen?.(); else if (document.fullscreenElement) document.exitFullscreen?.(); } catch { /* CSS still hides sidebar */ } }}>
            <Maximize className="w-3 h-3" /> Full Screen</button>
          <button type="button" data-testid="batcher-loader-btn" title="iPad view for the loader cab — both lanes, live weights, big buttons" className={`${toolBtn} !text-sky-300 !border-sky-700`}
            onClick={() => window.open("/loader", "gadtech-loader", "noopener")}>
            <Tablet className="w-3 h-3" /> Loader Screen</button>
          <button type="button" data-testid="batcher-popout-btn" title="Open on a second monitor" className={toolBtn}
            onClick={() => { const w = 1600, h = 1000; window.open("/batcher?popout=1", "gadtech-batcher-popout", `width=${w},height=${h},left=${(window.screen.availWidth - w) / 2},top=${(window.screen.availHeight - h) / 2},menubar=no,toolbar=no,location=no,status=no`); }}>
            <ExternalLink className="w-3 h-3" /> Pop Out</button>
        </div>
      </div>

      {/* High-visibility mix bar — locked at the top */}
      <MixBar batch={focus} mix={focusMix} mixes={mixes} lane={lane} onQtySaved={load} onMixChange={(mixId) => focus && changeMix(focus.id, mixId)} scaleFeed={scaleFeed} liveStatus={liveStatus} />

      {/* Two-column cockpit */}
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-1.5">
        <PlantColumn plantId={plantId} batches={batches} byLane={byLane} />
        <div className="flex flex-col min-h-0 min-w-0 gap-1.5">
          <div className="basis-[34%] shrink-0 min-h-0 flex flex-col">
            <TruckQueue batches={batches} byLane={byLane} mixes={mixes} selectedLane={lane} onSelectLane={pickLane} onAssign={(l, bid) => { assign(l, bid); pickLane(l); }} />
          </div>
          <LiveWeights
            lane={lane} batch={focus} mix={focusMix} byLane={byLane} onSelectLane={pickLane} scaleFeed={scaleFeed}
            onCapture={(key, value) => focus && capture(focus.id, key, value)}
            onSiloChange={(silo) => focus && setSilo(focus.id, silo)}
            onLoadTruck={() => focus && loadTruck(focus.id)}
            onComplete={(auto) => focus && complete(focus.id, auto === true)}
            onAbort={() => focus && abort(focus.id)}
            onAction={(p) => focus && doAction(focus.id, p)}
            onRefresh={load}
          />
        </div>
      </div>

      {/* v3.5r — AI Slump card lives behind the Slump AI icon in the header
          now so it stops eating vertical space on the console. */}
      <Dialog open={slumpOpen} onOpenChange={setSlumpOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-xl" data-testid="slump-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary-orange" />
              AI Slump Prediction
            </DialogTitle>
          </DialogHeader>
          <SlumpCard
            batch={batches.find((b) => b.status === "batching") || batches.find((b) => b.status === "queued")}
            mix={(() => {
              const cur = batches.find((b) => b.status === "batching") || batches.find((b) => b.status === "queued");
              return cur ? mixes.find((m) => m.id === cur.mix_id) : null;
            })()}
          />
        </DialogContent>
      </Dialog>

      {/* ─── Load Recall dialog · v3.5p ────────────────────────────────
          Recall every completed load for TODAY or THIS MONTH so the
          batcher can quickly audit what's been poured. */}
      <Dialog open={recallOpen} onOpenChange={setRecallOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-4xl" data-testid="recall-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <History className="w-5 h-5 text-primary-orange" />
              Load Recall
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 border-b border-industrial pb-3">
            <label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Range</label>
            <select
              data-testid="recall-range-select"
              value={recallRange}
              onChange={(e) => setRecallRange(e.target.value)}
              className="rounded-none bg-transparent border border-industrial mono text-xs px-2 py-1 uppercase tracking-widest hover:border-primary-orange"
            >
              <option value="today">Today</option>
              <option value="month">This Month</option>
            </select>
            <div className="ml-auto flex items-center gap-3">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                Loads · <span className="text-primary-orange font-black" data-testid="recall-count">{recallRows.length}</span>
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                Total · <span className="text-primary-orange font-black" data-testid="recall-total-m3">
                  {recallRows.reduce((s, r) => s + Number(r.quantity_m3 || 0), 0).toFixed(1)}
                </span> m³
              </div>
            </div>
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {recallLoading && (
              <div className="mono text-xs text-muted-industrial text-center py-6 uppercase tracking-widest">Loading…</div>
            )}
            {!recallLoading && recallRows.length === 0 && (
              <div className="mono text-xs text-muted-industrial text-center py-6 uppercase tracking-widest">
                No completed loads for this range
              </div>
            )}
            {!recallLoading && recallRows.length > 0 && (
              <table className="w-full mono text-[11px]" data-testid="recall-table">
                <thead className="border-b border-industrial">
                  <tr className="text-[9px] uppercase tracking-widest text-muted-industrial">
                    <th className="text-left py-1.5 px-2">Completed</th>
                    <th className="text-left py-1.5 px-2">Mix</th>
                    <th className="text-left py-1.5 px-2">Truck</th>
                    <th className="text-right py-1.5 px-2">Volume</th>
                    <th className="text-left py-1.5 px-2">Lane</th>
                    <th className="text-left py-1.5 px-2">Silo</th>
                    <th className="text-right py-1.5 px-2">Tol</th>
                  </tr>
                </thead>
                <tbody>
                  {recallRows.map((r) => {
                    const dt = r.completed_at || r.created_at || "";
                    const [d, tRaw] = dt.split("T");
                    const t = (tRaw || "").slice(0, 5);
                    return (
                      <tr key={r.id} className="border-b border-industrial/30 hover:bg-black/40" data-testid={`recall-row-${r.id}`}>
                        <td className="py-1.5 px-2">
                          <div className="text-ink">{d}</div>
                          <div className="text-[9px] text-muted-industrial">{t}</div>
                        </td>
                        <td className="py-1.5 px-2">
                          <button type="button" onClick={() => setDetail({ batch: r, mode: "weights" })} data-testid={`recall-mix-btn-${r.id}`}
                            title="Mix breakdown · batch weights target vs actual"
                            className="text-primary-orange font-black underline decoration-dotted underline-offset-2 hover:text-ink">{r.mix_code || "—"}</button>
                        </td>
                        <td className="py-1.5 px-2 text-ink">{r.truck_number || "—"}</td>
                        <td className="py-1.5 px-2 text-right text-ink font-black tabular-nums">{Number(r.quantity_m3 || 0).toFixed(1)} m³</td>
                        <td className="py-1.5 px-2 text-muted-industrial">{r.lane_id || "—"}</td>
                        <td className="py-1.5 px-2 text-muted-industrial">{r.cement_silo_id || "—"}</td>
                        <td className="py-1.5 px-2 text-right">
                          <button type="button" onClick={() => setDetail({ batch: r, mode: "tolerance" })} data-testid={`recall-tol-btn-${r.id}`}
                            title={r.within_tolerance ? "Specs met — view certificate" : "Out of tolerance — see which material failed"}
                            className={`px-1.5 py-0.5 border font-black hover:bg-black/30 ${r.within_tolerance ? "border-emerald-500/60 text-emerald-400" : "border-red-500/70 text-red-400"}`}>
                            {r.within_tolerance ? "OK" : "OOT"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button
              data-testid="recall-close"
              variant="outline"
              onClick={() => setRecallOpen(false)}
              className="rounded-none border-industrial uppercase text-xs"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {detail && <BatchDetailDialog batch={detail.batch} mode={detail.mode} onClose={() => setDetail(null)} />}
    </div>
  );
}

// Small helper — resolve lane_id for a batch id from a list

function byLaneIdOf(batches, bid) {
  return batches.find((b) => b.id === bid)?.lane_id || null;
}
